/**
 * Supplier Float Detail Overlay — Admin Portal (MMAP)
 * Complete deep-dive workspace for supplier prefunded float accounts.
 * Live balances, General Ledger sync, journal history, bank top-ups,
 * 3-way reconciliation runs, and threshold management.
 *
 * Enhanced with:
 * - Pay Supplier via SBSA PayShap RPP (from Treasury account 1100-01-01)
 * - Supplier Banking Details Management
 * - Executive SMS OTP Security Gate to André (+27825571055)
 *
 * @author MyMoolah Treasury Platform
 * @date 2026-09-05
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  RefreshCw,
  Wallet,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Scale,
  Landmark,
  Clock,
  Edit3,
  ExternalLink,
  ShieldCheck,
  FileText,
  Calendar,
  Layers,
  Check,
  Sliders,
  DollarSign,
  Send,
  Zap,
  KeyRound,
  Lock,
  Eye,
  EyeOff,
  Building2,
  AlertCircle,
} from 'lucide-react';
import { formatZAR, formatCredits, relativeTime } from '../../utils/format';
import { useAuth } from '../../contexts/AuthContext';

const API = axios.create({ baseURL: '/api/v1/admin' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const TREASURY_API = axios.create({ baseURL: '/api/v1/treasury' });
TREASURY_API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const SA_BANKS = [
  { name: 'Standard Bank', code: '051001' },
  { name: 'First National Bank (FNB)', code: '250655' },
  { name: 'Absa Bank', code: '632005' },
  { name: 'Nedbank', code: '198765' },
  { name: 'Capitec Bank', code: '470010' },
  { name: 'Investec Bank', code: '580105' },
  { name: 'Discovery Bank', code: '679000' },
  { name: 'TymeBank', code: '678910' },
  { name: 'African Bank', code: '430000' },
  { name: 'Bidvest Bank', code: '462005' },
  { name: 'Sasfin Bank', code: '683000' },
];

export type FloatHealth = 'green' | 'amber' | 'red';
export type FloatUnit = 'ZAR' | 'credits';

interface BankingDetailsData {
  accountHolderName: string | null;
  accountType: string | null;
  branchCode: string | null;
  bankName: string | null;
  accountNumber: string | null;
  beneficiaryReference: string | null;
  lastVerifiedAt: string | null;
  lastVerifiedBy: string | null;
}

interface SupplierData {
  id: number;
  supplierId: string;
  supplierName: string;
  floatAccountNumber: string;
  floatAccountName: string;
  ledgerAccountCode: string | null;
  status: string;
  isActive: boolean;
  settlementPeriod: string;
  settlementMethod: string;
  initialBalance: number | null;
  bankAccountNumber: string | null;
  bankCode: string | null;
  bankName: string | null;
  bankingDetails?: BankingDetailsData;
  metadata: Record<string, any>;
  lastSettlementAt: string | null;
  nextSettlementAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BalancesData {
  currentBalance: number;
  minimumBalance: number;
  maximumBalance: number | null;
  deficit: number;
  headroom?: number;
  isDeficit?: boolean;
  deficitAmount?: number;
  health: FloatHealth;
  unit: FloatUnit;
  liveGlBalance: number | null;
  mirrorBalance: number;
  glVariance: number;
  lastUpdated: string;
}

interface LedgerAccountData {
  id: number;
  code: string;
  name: string;
  type: string;
  normal_side: string;
  is_active: boolean;
}

interface JournalEntryLine {
  lineId: number;
  entryId: number;
  reference: string;
  description: string;
  dc: 'debit' | 'credit';
  amount: number;
  memo: string | null;
  postedAt: string;
  createdAt?: string;
  balanceAfter?: number;
}

interface TopUpEvent {
  id: number;
  classification: string;
  targetAccount: string | null;
  amount: number;
  currency: string;
  valueDate: string | null;
  bankReference: string | null;
  narrative: string | null;
  postingMode: string;
  status: string;
  journalReference: string | null;
  createdAt: string;
}

interface ReconRun {
  id: number;
  supplierId: string;
  supplierName: string;
  ledgerAccountCode: string | null;
  environment: string;
  runDate: string;
  leg1SupplierBalance: number | null;
  leg1Source: string | null;
  leg2LedgerBalance: number;
  leg3MirrorBalance: number;
  varianceSupplierLedger: number | null;
  varianceLedgerMirror: number;
  explainedVariance: number;
  unexplainedVariance: number;
  tolerance: number;
  status: string;
  reasons: any[];
  metrics: Record<string, any>;
  createdAt: string;
}

interface ReconBreak {
  id: number;
  breakType: string;
  amount: number | null;
  asOfDate: string | null;
  status: string;
  detectedAt: string;
  description: string | null;
}

interface FloatDetailPayload {
  supplier: SupplierData;
  balances: BalancesData;
  ledgerAccount: LedgerAccountData | null;
  ledgerActivity: {
    totalLines: number;
    totalDebits: number;
    totalCredits: number;
    recentEntries: JournalEntryLine[];
  };
  topUps: TopUpEvent[];
  reconciliation: {
    recentRuns: ReconRun[];
    activeBreaks: ReconBreak[];
    latestRun: ReconRun | null;
    leg1ManualReading: {
      balance: number;
      asOf: string;
      capturedBy: string | null;
      note: string | null;
      capturedAt?: string;
    } | null;
  };
  payshapFeePayable?: {
    accountCode: string;
    accountName: string;
    balance: number;
    subLedger?: {
      completedPayouts: number;
      feesCollected: number;
      varianceToGl: number;
    };
    recentEntries: JournalEntryLine[];
  } | null;
}

const HEALTH_CONFIG: Record<
  FloatHealth,
  {
    icon: typeof CheckCircle2;
    label: string;
    pillClass: string;
    iconClass: string;
    borderClass: string;
  }
> = {
  green: {
    icon: CheckCircle2,
    label: 'Healthy',
    pillClass: 'border border-[#86BE41]/25 bg-[#86BE41]/10 text-[#5a8f2a]',
    iconClass: 'text-[var(--primary)]',
    borderClass: 'border-l-[var(--primary)]',
  },
  amber: {
    icon: AlertTriangle,
    label: 'Warning',
    pillClass: 'border border-[#2D8CCA]/25 bg-[#2D8CCA]/10 text-[#1a5f94]',
    iconClass: 'text-[var(--mymoolah-blue)]',
    borderClass: 'border-l-[var(--mymoolah-blue)]',
  },
  red: {
    icon: XCircle,
    label: 'Critical',
    pillClass: 'border border-red-200 bg-red-50 text-red-700',
    iconClass: 'text-[var(--destructive)]',
    borderClass: 'border-l-[var(--destructive)]',
  },
};

function formatFloatAmount(value: number, unit: FloatUnit = 'ZAR', signed = false): string {
  const formatted = unit === 'credits' ? formatCredits(value) : formatZAR(value);
  if (!signed) return formatted;
  if (value > 0) return `+${formatted}`;
  return formatted;
}

function maskAccountNumber(acc: string | null | undefined): string {
  const str = String(acc || '').trim();
  if (!str) return 'Not configured';
  if (str.length <= 4) return str;
  return '•••• ' + str.slice(-4);
}

function formatOtpCountdown(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function formatCalendarDate(value: string | null | undefined): string {
  if (!value) return '—';
  const datePart = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return new Date(`${datePart}T12:00:00.000Z`).toLocaleDateString('en-ZA');
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-ZA');
}

function formatJournalPostedAt(line: JournalEntryLine): string {
  const iso = line.createdAt || line.postedAt;
  if (!iso) return '—';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('en-ZA');
}

export const SupplierFloatDetailOverlay: React.FC = () => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [data, setData] = useState<FloatDetailPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'ledger' | 'topups' | 'recon' | 'settings'>('ledger');

  // Threshold modal state
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [minBalanceInput, setMinBalanceInput] = useState('');
  const [maxBalanceInput, setMaxBalanceInput] = useState('');
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [thresholdMsg, setThresholdMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Manual Leg1 reading capture state
  const [leg1BalanceInput, setLeg1BalanceInput] = useState('');
  const [leg1AsOfInput, setLeg1AsOfInput] = useState('');
  const [leg1NoteInput, setLeg1NoteInput] = useState('');
  const [savingLeg1, setSavingLeg1] = useState(false);
  const [leg1Msg, setLeg1Msg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Pay Supplier (PayShap RPP Top-Up) Modal State
  const [showPayModal, setShowPayModal] = useState(false);
  const [payAmountInput, setPayAmountInput] = useState('');
  const [payReferenceInput, setPayReferenceInput] = useState('');
  const [payStep, setPayStep] = useState<'form' | 'otp' | 'success'>('form');
  const [payOtpInput, setPayOtpInput] = useState('');
  const [payOtpSecondsLeft, setPayOtpSecondsLeft] = useState<number | null>(null);
  const [requestingPayOtp, setRequestingPayOtp] = useState(false);
  const [executingPay, setExecutingPay] = useState(false);
  const [payMsg, setPayMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [payResult, setPayResult] = useState<any | null>(null);
  const [payOtpPhoneMasked, setPayOtpPhoneMasked] = useState('+27 82 *** *055');

  // Edit Bank Details Modal State
  const [showBankModal, setShowBankModal] = useState(false);
  const [bankNameInput, setBankNameInput] = useState('');
  const [bankCodeInput, setBankCodeInput] = useState('');
  const [bankAccountInput, setBankAccountInput] = useState('');
  const [accountHolderInput, setAccountHolderInput] = useState('');
  const [accountTypeInput, setAccountTypeInput] = useState('current');
  const [beneficiaryRefInput, setBeneficiaryRefInput] = useState('');
  const [bankStep, setBankStep] = useState<'form' | 'otp' | 'success'>('form');
  const [bankOtpInput, setBankOtpInput] = useState('');
  const [bankOtpSecondsLeft, setBankOtpSecondsLeft] = useState<number | null>(null);
  const [requestingBankOtp, setRequestingBankOtp] = useState(false);
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [bankMsg, setBankMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [bankOtpPhoneMasked, setBankOtpPhoneMasked] = useState('+27 82 *** *055');
  const [showBankFullAcc, setShowBankFullAcc] = useState(false);

  // Countdown timer for Pay OTP (180s TTL)
  useEffect(() => {
    if (payOtpSecondsLeft === null || payOtpSecondsLeft <= 0) return;
    const timer = setInterval(() => {
      setPayOtpSecondsLeft((prev) => (prev !== null && prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [payOtpSecondsLeft]);

  // Countdown timer for Bank Details OTP (180s TTL)
  useEffect(() => {
    if (bankOtpSecondsLeft === null || bankOtpSecondsLeft <= 0) return;
    const timer = setInterval(() => {
      setBankOtpSecondsLeft((prev) => (prev !== null && prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [bankOtpSecondsLeft]);

  const fetchDetail = useCallback(async (isSilent = false) => {
    if (!supplierId) return;
    if (!isSilent) setLoading(true);
    else setRefreshing(true);
    setError(null);

    try {
      const res = await API.get(`/floats/${encodeURIComponent(supplierId)}`);
      if (res.data?.success) {
        setData(res.data.data);
        setMinBalanceInput(String(res.data.data.balances.minimumBalance || 0));
        setMaxBalanceInput(res.data.data.balances.maximumBalance ? String(res.data.data.balances.maximumBalance) : '');
      } else {
        setError(res.data?.error || 'Failed to load supplier float details');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Error connecting to server');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [supplierId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleUpdateThreshold = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supplierId) return;
    setSavingThreshold(true);
    setThresholdMsg(null);

    try {
      const minVal = parseFloat(minBalanceInput);
      const maxVal = maxBalanceInput.trim() !== '' ? parseFloat(maxBalanceInput) : null;

      if (isNaN(minVal) || minVal < 0) {
        setThresholdMsg({ type: 'error', text: 'Minimum balance must be a non-negative number' });
        setSavingThreshold(false);
        return;
      }

      if (maxVal !== null && (isNaN(maxVal) || maxVal < minVal)) {
        setThresholdMsg({ type: 'error', text: 'Maximum balance must be greater than or equal to minimum balance' });
        setSavingThreshold(false);
        return;
      }

      const res = await API.put(`/floats/${encodeURIComponent(supplierId)}/threshold`, {
        minimumBalance: minVal,
        maximumBalance: maxVal,
      });

      if (res.data?.success) {
        setThresholdMsg({ type: 'success', text: 'Threshold updated successfully' });
        setTimeout(() => {
          setShowThresholdModal(false);
          setThresholdMsg(null);
        }, 1200);
        await fetchDetail(true);
      } else {
        setThresholdMsg({ type: 'error', text: res.data?.error || 'Failed to update threshold' });
      }
    } catch (err: any) {
      setThresholdMsg({ type: 'error', text: err?.response?.data?.error || err.message || 'Update failed' });
    } finally {
      setSavingThreshold(false);
    }
  };

  const handleCaptureLeg1 = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supplierId) return;
    setSavingLeg1(true);
    setLeg1Msg(null);

    try {
      const bal = parseFloat(leg1BalanceInput);
      if (isNaN(bal) || bal < 0) {
        setLeg1Msg({ type: 'error', text: 'Balance reading must be a non-negative number' });
        setSavingLeg1(false);
        return;
      }

      const res = await API.post(`/floats/${encodeURIComponent(supplierId)}/leg1-reading`, {
        balance: bal,
        asOf: leg1AsOfInput || new Date().toISOString(),
        note: leg1NoteInput.trim() || undefined,
      });

      if (res.data?.success) {
        setLeg1Msg({ type: 'success', text: 'Manual reading recorded successfully' });
        setLeg1BalanceInput('');
        setLeg1NoteInput('');
        await fetchDetail(true);
      } else {
        setLeg1Msg({ type: 'error', text: res.data?.error || 'Failed to capture reading' });
      }
    } catch (err: any) {
      setLeg1Msg({ type: 'error', text: err?.response?.data?.error || err.message || 'Recording failed' });
    } finally {
      setSavingLeg1(false);
    }
  };

  // Open Pay Supplier Modal
  const openPayModal = () => {
    if (!data) return;
    const deficitAmount = data.balances.minimumBalance > data.balances.currentBalance
      ? data.balances.minimumBalance - data.balances.currentBalance
      : 0;
    setPayAmountInput(deficitAmount > 0 ? String(deficitAmount.toFixed(2)) : '1000.00');
    setPayReferenceInput('');
    setPayStep('form');
    setPayOtpInput('');
    setPayOtpSecondsLeft(null);
    setPayMsg(null);
    setPayResult(null);
    setShowPayModal(true);
  };

  // Request Pay OTP
  const handleRequestPayOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    const amt = parseFloat(payAmountInput);
    if (isNaN(amt) || amt <= 0) {
      setPayMsg({ type: 'error', text: 'Please enter an amount greater than R0.00' });
      return;
    }
    if (amt > 50000) {
      setPayMsg({ type: 'error', text: 'Maximum PayShap RPP single payment is R50,000.00' });
      return;
    }

    setRequestingPayOtp(true);
    setPayMsg(null);

    try {
      const res = await TREASURY_API.post(`/suppliers/${encodeURIComponent(data.supplier.supplierId)}/pay/request-otp`, {
        amount: amt,
        reference: payReferenceInput.trim() || undefined,
      });

      if (res.data?.success && res.data.smsDispatched !== false) {
        const masked = res.data.recipientPhoneMasked || payOtpPhoneMasked;
        setPayOtpPhoneMasked(masked);
        setPayStep('otp');
        setPayOtpSecondsLeft(res.data.expiresInSeconds || 180);
        setPayMsg({
          type: 'success',
          text: `Approval OTP sent via SMS to André Botes (${masked}). Valid for 3 minutes.`,
        });
      } else {
        setPayMsg({
          type: 'error',
          text: res.data?.message || res.data?.error || 'Authorization SMS was not dispatched. OTP was not sent.',
        });
      }
    } catch (err: any) {
      setPayMsg({
        type: 'error',
        text: err?.response?.data?.message || err?.response?.data?.error || err.message || 'Error requesting OTP',
      });
    } finally {
      setRequestingPayOtp(false);
    }
  };

  // Verify OTP and Execute Payment
  const handleExecutePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    if (payOtpInput.trim().length !== 6) {
      setPayMsg({ type: 'error', text: 'Please enter the 6-digit OTP code' });
      return;
    }

    setExecutingPay(true);
    setPayMsg(null);

    try {
      const res = await TREASURY_API.post(`/suppliers/${encodeURIComponent(data.supplier.supplierId)}/pay/verify-and-execute`, {
        amount: parseFloat(payAmountInput),
        reference: payReferenceInput.trim() || undefined,
        otp: payOtpInput.trim(),
      });

      if (res.data?.success) {
        setPayResult(res.data.data);
        setPayStep('success');
        await fetchDetail(true);
      } else {
        setPayMsg({ type: 'error', text: res.data?.message || 'Payment execution failed' });
      }
    } catch (err: any) {
      setPayMsg({
        type: 'error',
        text: err?.response?.data?.message || err?.response?.data?.error || err.message || 'Payment failed',
      });
    } finally {
      setExecutingPay(false);
    }
  };

  // Open Bank Details Modal
  const openBankModal = () => {
    if (!data) return;
    const sup = data.supplier;
    setBankNameInput(sup.bankName || 'Standard Bank');
    setBankCodeInput(sup.bankCode || '051001');
    setBankAccountInput(sup.bankAccountNumber || '');
    setAccountHolderInput(sup.bankingDetails?.accountHolderName || sup.supplierName || '');
    setAccountTypeInput(sup.bankingDetails?.accountType || 'current');
    setBeneficiaryRefInput(sup.bankingDetails?.beneficiaryReference || sup.supplierId || '');
    setBankStep('form');
    setBankOtpInput('');
    setBankOtpSecondsLeft(null);
    setBankMsg(null);
    setShowBankModal(true);
  };

  // Request Bank Details OTP
  const handleRequestBankOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    if (!bankNameInput.trim()) {
      setBankMsg({ type: 'error', text: 'Bank name is required' });
      return;
    }
    if (!bankCodeInput.trim()) {
      setBankMsg({ type: 'error', text: 'Branch code is required' });
      return;
    }
    if (!bankAccountInput.trim()) {
      setBankMsg({ type: 'error', text: 'Bank account number is required' });
      return;
    }
    if (!accountHolderInput.trim()) {
      setBankMsg({ type: 'error', text: 'Account holder name is required' });
      return;
    }

    setRequestingBankOtp(true);
    setBankMsg(null);

    try {
      const res = await TREASURY_API.post(`/suppliers/${encodeURIComponent(data.supplier.supplierId)}/bank-details/request-otp`, {
        bankName: bankNameInput.trim(),
        bankCode: bankCodeInput.trim(),
        bankAccountNumber: bankAccountInput.trim(),
        accountHolderName: accountHolderInput.trim(),
        accountType: accountTypeInput,
        beneficiaryReference: beneficiaryRefInput.trim() || undefined,
      });

      if (res.data?.success && res.data.smsDispatched !== false) {
        const masked = res.data.recipientPhoneMasked || bankOtpPhoneMasked;
        setBankOtpPhoneMasked(masked);
        setBankStep('otp');
        setBankOtpSecondsLeft(res.data.expiresInSeconds || 180);
        setBankMsg({
          type: 'success',
          text: `Approval OTP sent via SMS to André Botes (${masked}). Valid for 3 minutes.`,
        });
      } else {
        setBankMsg({
          type: 'error',
          text: res.data?.message || res.data?.error || 'Authorization SMS was not dispatched. OTP was not sent.',
        });
      }
    } catch (err: any) {
      setBankMsg({
        type: 'error',
        text: err?.response?.data?.message || err?.response?.data?.error || err.message || 'Error requesting OTP',
      });
    } finally {
      setRequestingBankOtp(false);
    }
  };

  // Verify OTP and Save Bank Details
  const handleSaveBankDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    if (bankOtpInput.trim().length !== 6) {
      setBankMsg({ type: 'error', text: 'Please enter the 6-digit OTP code' });
      return;
    }

    setSavingBankDetails(true);
    setBankMsg(null);

    try {
      const res = await TREASURY_API.put(`/suppliers/${encodeURIComponent(data.supplier.supplierId)}/bank-details`, {
        bankName: bankNameInput.trim(),
        bankCode: bankCodeInput.trim(),
        bankAccountNumber: bankAccountInput.trim(),
        accountHolderName: accountHolderInput.trim(),
        accountType: accountTypeInput,
        beneficiaryReference: beneficiaryRefInput.trim() || undefined,
        otp: bankOtpInput.trim(),
      });

      if (res.data?.success) {
        setBankMsg({ type: 'success', text: 'Supplier banking details updated and verified successfully' });
        setTimeout(() => {
          setShowBankModal(false);
          setBankMsg(null);
        }, 1500);
        await fetchDetail(true);
      } else {
        setBankMsg({ type: 'error', text: res.data?.message || 'Update failed' });
      }
    } catch (err: any) {
      setBankMsg({
        type: 'error',
        text: err?.response?.data?.message || err?.response?.data?.error || err.message || 'Update failed',
      });
    } finally {
      setSavingBankDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center space-y-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[var(--muted)] border-t-[var(--primary)]" />
        <p className="text-sm font-medium text-[var(--muted-foreground)]">Loading supplier float workspace...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-1.5 text-sm font-medium text-[var(--mymoolah-blue)] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
          <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-red-500" />
          <h2 className="text-lg font-semibold">Unable to load supplier float</h2>
          <p className="mt-1 text-sm">{error || 'Supplier not found'}</p>
          <button
            type="button"
            onClick={() => fetchDetail()}
            className="mt-4 rounded-lg bg-[var(--primary)] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[var(--primary)]/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { supplier, balances, ledgerAccount, ledgerActivity, topUps, reconciliation, payshapFeePayable } = data;
  const isTreasury = String(supplier.supplierId || '').toUpperCase() === 'MMTP_TREASURY';
  const healthCfg = HEALTH_CONFIG[balances.health] || HEALTH_CONFIG.amber;
  const HealthIcon = healthCfg.icon;

  return (
    <div className="space-y-6 pb-12">
      {/* 1. Header Navigation & Supplier Title */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate('/admin/dashboard')}
            className="mb-2 flex items-center gap-1.5 text-xs font-medium text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Operational Dashboard
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-[var(--foreground)]">
              {supplier.supplierName}
            </h1>
            <span className="font-mono rounded-md border border-[var(--border)] bg-[var(--muted)] px-2.5 py-0.5 text-xs font-semibold text-[var(--foreground)]">
              {supplier.supplierId}
            </span>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                supplier.status === 'active'
                  ? 'border border-[#86BE41]/25 bg-[#86BE41]/10 text-[#5a8f2a]'
                  : 'border border-amber-200 bg-amber-50 text-amber-700'
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              {supplier.status.toUpperCase()}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${healthCfg.pillClass}`}
            >
              <HealthIcon className={`h-3.5 w-3.5 ${healthCfg.iconClass}`} aria-hidden />
              {healthCfg.label}
            </span>
          </div>
          <p className="mt-1 text-xs text-[var(--muted-foreground)]">
            Operational float account: <span className="font-mono font-medium">{supplier.floatAccountNumber}</span> ·{' '}
            {supplier.floatAccountName}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && !isTreasury && (
            <button
              type="button"
              onClick={openPayModal}
              disabled={!supplier.isActive || supplier.status !== 'active'}
              className="flex items-center gap-1.5 rounded-lg bg-[var(--primary)] px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[var(--primary)]/90 disabled:opacity-50"
            >
              <Zap className="h-3.5 w-3.5 fill-current" />
              Pay Supplier (PayShap RPP)
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowThresholdModal(true)}
              className="flex items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3.5 py-2 text-xs font-semibold text-[var(--foreground)] shadow-sm hover:bg-[var(--muted)]"
            >
              <Edit3 className="h-3.5 w-3.5 text-[var(--muted-foreground)]" />
              Edit Threshold
            </button>
          )}
          <button
            type="button"
            onClick={() => fetchDetail(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3.5 py-2 text-xs font-semibold text-[var(--foreground)] shadow-sm hover:bg-[var(--muted)] disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* 2. Top Metric Cards (4-column grid) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Card 1: Float Balance */}
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              Float Balance
            </span>
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--primary)]/10">
              <Wallet className="h-4 w-4 text-[var(--primary)]" />
            </div>
          </div>
          <p className="mt-2 font-mono text-2xl font-bold tracking-tight text-[var(--foreground)]">
            {formatFloatAmount(balances.currentBalance, balances.unit)}
          </p>
          <div className="mt-2 flex items-center justify-between text-xs text-[var(--muted-foreground)]">
            <span>Mirror: {formatFloatAmount(balances.mirrorBalance, balances.unit)}</span>
            <span>{relativeTime(balances.lastUpdated)}</span>
          </div>
        </div>

        {/* Card 2: Minimum Floor Threshold */}
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              Min Balance
            </span>
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--mymoolah-blue)]/10">
              <ShieldCheck className="h-4 w-4 text-[var(--mymoolah-blue)]" />
            </div>
          </div>
          <p className="mt-2 font-mono text-2xl font-bold tracking-tight text-[var(--foreground)]">
            {formatFloatAmount(balances.minimumBalance, balances.unit)}
          </p>
          <div className="mt-2 flex items-center justify-between text-xs text-[var(--muted-foreground)]">
            <span>Capacity: {balances.maximumBalance ? formatFloatAmount(balances.maximumBalance, balances.unit) : 'Uncapped'}</span>
            <button
              type="button"
              onClick={() => setShowThresholdModal(true)}
              className="text-[var(--primary)] hover:underline font-medium"
            >
              Adjust
            </button>
          </div>
        </div>

        {/* Card 3: Headroom or Deficit */}
        {(() => {
          const isDeficit = balances.deficit < 0;
          const pctAboveMin = balances.minimumBalance > 0
            ? (((balances.currentBalance - balances.minimumBalance) / balances.minimumBalance) * 100).toFixed(0)
            : '0';

          return (
            <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
                  {isDeficit ? 'Deficit Below Floor' : 'Headroom'}
                </span>
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                    isDeficit ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'
                  }`}
                >
                  <Scale className="h-4 w-4" />
                </div>
              </div>
              <p
                className={`mt-2 font-mono text-2xl font-bold tracking-tight ${
                  isDeficit ? 'text-[var(--destructive)]' : 'text-[var(--success-color)]'
                }`}
              >
                {formatFloatAmount(balances.deficit, balances.unit, true)}
              </p>
              <div className="mt-2 flex items-center justify-between text-xs text-[var(--muted-foreground)]">
                <span>
                  {isDeficit
                    ? 'Requires top-up'
                    : `${pctAboveMin}% above min floor`}
                </span>
                {isDeficit && isAdmin && !isTreasury && (
                  <button
                    type="button"
                    onClick={openPayModal}
                    className="font-semibold text-[var(--primary)] hover:underline flex items-center gap-1"
                  >
                    <Zap className="h-3 w-3" /> Top Up Now
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Card 4: General Ledger Sync */}
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              Live GL Balance
            </span>
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--muted)]">
              <Landmark className="h-4 w-4 text-[var(--foreground)]" />
            </div>
          </div>
          <p className="mt-2 font-mono text-2xl font-bold tracking-tight text-[var(--foreground)]">
            {balances.liveGlBalance !== null ? formatZAR(balances.liveGlBalance) : 'No GL Code'}
          </p>
          <div className="mt-2 flex items-center justify-between text-xs">
            <span
              className={`font-mono font-medium ${
                balances.glVariance === 0 ? 'text-[#5a8f2a]' : 'text-amber-600'
              }`}
            >
              {balances.glVariance === 0
                ? 'Variance: R 0,00'
                : `Variance: ${formatZAR(balances.glVariance)}`}
            </span>
            <span className="rounded bg-[var(--muted)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--muted-foreground)]">
              {reconciliation.latestRun?.status || 'Active'}
            </span>
          </div>
        </div>
      </div>

      {payshapFeePayable ? (
        <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] shadow-sm">
          <div className="border-b border-[var(--border)] bg-[var(--muted)]/30 px-5 py-3">
            <h2 className="text-sm font-semibold text-[var(--foreground)]">OTT PayShap fee payable</h2>
            <p className="mt-0.5 text-xs text-[var(--muted-foreground)]">
              R2,50 Instant Payment fees collected from the wallet. Not part of the merchant float
              ({payshapFeePayable.accountCode}). Settlement method to OTT is still to be confirmed.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 p-5 lg:grid-cols-3">
            <div>
              <p className="text-xs text-[var(--muted-foreground)]">Amount owed to OTT</p>
              <p className="mt-1 font-mono text-2xl font-bold text-[var(--foreground)]">
                {formatZAR(payshapFeePayable.balance)}
              </p>
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">{payshapFeePayable.accountName}</p>
              {payshapFeePayable.subLedger ? (
                <div className="mt-3 space-y-1 text-xs text-[var(--muted-foreground)]">
                  <p>
                    Sub-ledger: {payshapFeePayable.subLedger.completedPayouts} completed PayShap
                    {' '}({formatZAR(payshapFeePayable.subLedger.feesCollected)})
                  </p>
                  <p
                    className={
                      payshapFeePayable.subLedger.varianceToGl === 0
                        ? 'text-[#5a8f2a]'
                        : 'text-amber-600'
                    }
                  >
                    {payshapFeePayable.subLedger.varianceToGl === 0
                      ? 'GL matches completed PayShap fees'
                      : `GL vs sub-ledger: ${formatZAR(payshapFeePayable.subLedger.varianceToGl)}`}
                  </p>
                </div>
              ) : null}
            </div>
            <div className="lg:col-span-2 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-[var(--muted-foreground)]">
                  <tr>
                    <th className="pb-2 font-semibold">Posted</th>
                    <th className="px-2 pb-2 font-semibold">Reference</th>
                    <th className="pb-2 text-right font-semibold">Credit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)] font-mono">
                  {(payshapFeePayable.recentEntries || []).length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-3 font-sans text-[var(--muted-foreground)]">
                        No PayShap fee journals yet.
                      </td>
                    </tr>
                  ) : (
                    payshapFeePayable.recentEntries.map((line) => (
                      <tr key={line.lineId}>
                        <td className="whitespace-nowrap py-2 font-sans text-[var(--muted-foreground)]">
                          {new Date(line.postedAt).toLocaleString('en-ZA')}
                        </td>
                        <td className="px-2 py-2 font-semibold text-[var(--primary)]">{line.reference}</td>
                        <td className="whitespace-nowrap py-2 text-right">
                          {line.dc === 'credit' ? formatZAR(line.amount) : `−${formatZAR(line.amount)}`}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {/* 3. Account Configuration Summary Card */}
      <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] shadow-sm">
        <div className="border-b border-[var(--border)] bg-[var(--muted)]/30 px-5 py-3">
          <h2 className="text-sm font-semibold text-[var(--foreground)]">Account & Settlement Configuration</h2>
        </div>
        <div className="grid grid-cols-1 gap-6 p-5 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-xs text-[var(--muted-foreground)]">General Ledger Account</p>
            <p className="mt-1 font-mono text-sm font-semibold text-[var(--foreground)]">
              {supplier.ledgerAccountCode || 'None assigned'}
            </p>
            <p className="text-xs text-[var(--muted-foreground)]">
              {ledgerAccount?.name || 'No ledger mapping'}
            </p>
          </div>
          <div>
            <p className="text-xs text-[var(--muted-foreground)]">Settlement Model</p>
            <p className="mt-1 text-sm font-semibold capitalize text-[var(--foreground)]">
              {supplier.settlementMethod} · {supplier.settlementPeriod.replace('_', ' ')}
            </p>
            <p className="text-xs text-[var(--muted-foreground)]">
              Initial: {formatFloatAmount(parseFloat(supplier.initialBalance as any) || 0, balances.unit)}
            </p>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-[var(--muted-foreground)]">Bank Settlement Details</p>
              {isAdmin && !isTreasury && (
                <button
                  type="button"
                  onClick={openBankModal}
                  className="text-[11px] font-semibold text-[var(--primary)] hover:underline flex items-center gap-1"
                >
                  <Edit3 className="h-3 w-3" /> Edit
                </button>
              )}
            </div>
            <p className="mt-1 text-sm font-semibold text-[var(--foreground)]">
              {supplier.bankName || 'Not configured'}
            </p>
            <p className="font-mono text-xs text-[var(--muted-foreground)]">
              Acc: {maskAccountNumber(supplier.bankAccountNumber)} · Code: {supplier.bankCode || 'N/A'}
            </p>
            <p className="text-[11px] text-[var(--muted-foreground)]">
              Ref: <span className="font-mono font-semibold text-[var(--foreground)]">{supplier.bankingDetails?.beneficiaryReference || supplier.supplierId}</span>
            </p>
            {supplier.bankingDetails?.accountHolderName && (
              <p className="text-[11px] text-[var(--muted-foreground)] truncate">
                Holder: {supplier.bankingDetails.accountHolderName}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-[var(--muted-foreground)]">Reconciliation Alignment</p>
            <p className="mt-1 text-sm font-semibold capitalize text-[var(--foreground)]">
              {reconciliation.latestRun ? reconciliation.latestRun.status.replace(/_/g, ' ') : 'Balanced'}
            </p>
            <p className="text-xs text-[var(--muted-foreground)]">
              Tolerance: R {reconciliation.latestRun?.tolerance || 0.01}
            </p>
          </div>
        </div>
      </div>

      {/* 4. Deep-Dive Tab Navigation */}
      <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] shadow-sm">
        <div className="flex border-b border-[var(--border)] bg-[var(--muted)]/20 px-4">
          <button
            type="button"
            onClick={() => setActiveTab('ledger')}
            className={`flex items-center gap-2 border-b-2 px-4 py-3.5 text-xs font-semibold transition-colors ${
              activeTab === 'ledger'
                ? 'border-[var(--primary)] text-[var(--primary)]'
                : 'border-transparent text-[var(--muted-foreground)] hover:text-[var(--foreground)]'
            }`}
          >
            <FileText className="h-4 w-4" />
            Ledger Activity
            <span className="rounded-full bg-[var(--muted)] px-2 py-0.5 text-[10px] text-[var(--foreground)]">
              {ledgerActivity.totalLines}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('topups')}
            className={`flex items-center gap-2 border-b-2 px-4 py-3.5 text-xs font-semibold transition-colors ${
              activeTab === 'topups'
                ? 'border-[var(--primary)] text-[var(--primary)]'
                : 'border-transparent text-[var(--muted-foreground)] hover:text-[var(--foreground)]'
            }`}
          >
            <Zap className="h-4 w-4" />
            Outbound Top-Ups & Bank Prefunding
            <span className="rounded-full bg-[var(--muted)] px-2 py-0.5 text-[10px] text-[var(--foreground)]">
              {topUps.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('recon')}
            className={`flex items-center gap-2 border-b-2 px-4 py-3.5 text-xs font-semibold transition-colors ${
              activeTab === 'recon'
                ? 'border-[var(--primary)] text-[var(--primary)]'
                : 'border-transparent text-[var(--muted-foreground)] hover:text-[var(--foreground)]'
            }`}
          >
            <Scale className="h-4 w-4" />
            3-Way Reconciliation History
            {reconciliation.activeBreaks.length > 0 && (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
                {reconciliation.activeBreaks.length} break
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('settings')}
            className={`flex items-center gap-2 border-b-2 px-4 py-3.5 text-xs font-semibold transition-colors ${
              activeTab === 'settings'
                ? 'border-[var(--primary)] text-[var(--primary)]'
                : 'border-transparent text-[var(--muted-foreground)] hover:text-[var(--foreground)]'
            }`}
          >
            <Sliders className="h-4 w-4" />
            Settings & Controls
          </button>
        </div>

        {/* Tab 1: Ledger Activity */}
        {activeTab === 'ledger' && (
          <div className="p-5">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[var(--foreground)]">
                  Posted Double-Entry Journal Lines (Account {supplier.ledgerAccountCode || 'N/A'})
                </h3>
                <p className="text-xs text-[var(--muted-foreground)]">
                  Total Debits: {formatZAR(ledgerActivity.totalDebits)} · Total Credits: {formatZAR(ledgerActivity.totalCredits)}
                </p>
                <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                  Posted Date is when the journal was written to our books. Bank value date is on the Outbound Top-Ups tab.
                </p>
              </div>
              <span className="text-xs text-[var(--muted-foreground)]">Showing latest 50 entries</span>
            </div>

            {ledgerActivity.recentEntries.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[var(--border)] p-8 text-center text-xs text-[var(--muted-foreground)]">
                No posted journal entries found for account {supplier.ledgerAccountCode || 'N/A'}.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[var(--muted)]/50 text-[var(--muted-foreground)]">
                    <tr>
                      <th scope="col" className="px-4 py-2.5 font-semibold">Posted Date</th>
                      <th scope="col" className="px-4 py-2.5 font-semibold">Reference</th>
                      <th scope="col" className="px-4 py-2.5 font-semibold">Description</th>
                      <th scope="col" className="px-4 py-2.5 font-semibold">Memo</th>
                      <th scope="col" className="px-4 py-2.5 text-right font-semibold">Debit</th>
                      <th scope="col" className="px-4 py-2.5 text-right font-semibold">Credit</th>
                      <th scope="col" className="px-4 py-2.5 text-right font-semibold">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border)] bg-[var(--card)] font-mono">
                    {ledgerActivity.recentEntries.map((line) => (
                      <tr key={line.lineId} className="hover:bg-[var(--muted)]/20">
                        <td className="whitespace-nowrap px-4 py-2.5 text-[var(--muted-foreground)] font-sans">
                          {formatJournalPostedAt(line)}
                        </td>
                        <td className="px-4 py-2.5 font-semibold text-[var(--primary)]">{line.reference}</td>
                        <td className="px-4 py-2.5 font-sans text-[var(--foreground)]">{line.description}</td>
                        <td className="px-4 py-2.5 font-sans text-[var(--muted-foreground)]">{line.memo || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-[#5a8f2a]">
                          {line.dc === 'debit' ? formatZAR(line.amount) : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-[var(--destructive)]">
                          {line.dc === 'credit' ? formatZAR(line.amount) : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right font-semibold text-[var(--foreground)]">
                          {typeof line.balanceAfter === 'number' ? formatZAR(line.balanceAfter) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Outbound Top-Ups & Bank Prefunding */}
        {activeTab === 'topups' && (
          <div className="p-5">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[var(--foreground)]">
                  SBSA Bank Outbound Debit Events & Direct Float Prefunding
                </h3>
                <p className="text-xs text-[var(--muted-foreground)]">
                  Classified statement bank debit lines and PayShap RPP top-ups mapped to this supplier float.
                </p>
              </div>
              {isAdmin && !isTreasury && (
                <button
                  type="button"
                  onClick={openPayModal}
                  className="flex items-center gap-1.5 rounded-lg bg-[var(--primary)] px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-[var(--primary)]/90"
                >
                  <Zap className="h-3.5 w-3.5 fill-current" />
                  New PayShap Top-Up
                </button>
              )}
            </div>

            {topUps.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[var(--border)] p-8 text-center text-xs text-[var(--muted-foreground)]">
                No outbound debit prefunding events recorded yet.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[var(--muted)]/50 text-[var(--muted-foreground)]">
                    <tr>
                      <th className="px-4 py-2.5 font-semibold">Value Date</th>
                      <th className="px-4 py-2.5 font-semibold">Classification</th>
                      <th className="px-4 py-2.5 font-semibold">Bank Reference</th>
                      <th className="px-4 py-2.5 font-semibold">Narrative</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Amount (ZAR)</th>
                      <th className="px-4 py-2.5 font-semibold">Posting Mode</th>
                      <th className="px-4 py-2.5 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border)] bg-[var(--card)]">
                    {topUps.map((evt) => (
                      <tr key={evt.id} className="hover:bg-[var(--muted)]/20">
                        <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[var(--muted-foreground)]">
                          {formatCalendarDate(evt.valueDate || evt.createdAt)}
                        </td>
                        <td className="px-4 py-2.5 font-semibold text-[var(--foreground)]">
                          {evt.classification.replace(/_/g, ' ')}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-[var(--primary)]">
                          {evt.bankReference || '—'}
                        </td>
                        <td className="max-w-xs truncate px-4 py-2.5 text-[var(--muted-foreground)]">
                          {evt.narrative || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono font-bold text-[#5a8f2a]">
                          {formatZAR(evt.amount)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[11px] text-[var(--muted-foreground)]">
                          {evt.postingMode}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                              evt.status === 'auto_posted' || evt.status === 'approved'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : evt.status === 'pending_review'
                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : 'bg-[var(--muted)] text-[var(--muted-foreground)]'
                            }`}
                          >
                            {evt.status.toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab 3: 3-Way Reconciliation History */}
        {activeTab === 'recon' && (
          <div className="p-5 space-y-6">
            <p className="text-xs text-[var(--muted-foreground)]">
              This screen shows two separate jobs: open exception-queue items from bank prefunding and consumption recon, and daily Leg 1 / Leg 2 / Leg 3 snapshots. They are not the same run.
            </p>

            {/* Active Breaks Banner */}
            {reconciliation.activeBreaks.length > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <h4 className="text-sm font-semibold text-red-800">
                    Open prefunding / consumption exceptions ({reconciliation.activeBreaks.length})
                  </h4>
                </div>
                <p className="mt-1 text-xs text-red-700/80">
                  These are control variances to triage. They are not proof of missing cash until investigated.
                </p>
                <div className="mt-2 space-y-2 text-xs text-red-700">
                  {reconciliation.activeBreaks.map((b) => (
                    <div key={b.id} className="flex items-start justify-between gap-4">
                      <div>
                        <span>
                          Break #{b.id} · Type: {b.breakType.replace(/_/g, ' ')}
                          {b.asOfDate ? ` · As of ${formatCalendarDate(b.asOfDate)}` : ''}
                        </span>
                        {b.description && (
                          <p className="mt-0.5 text-red-600/80">{b.description}</p>
                        )}
                      </div>
                      <span className="shrink-0 font-mono font-bold">
                        {b.amount !== null ? formatZAR(b.amount) : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-[var(--foreground)]">
                    Historical 3-Way Reconciliation Runs
                  </h3>
                  <p className="text-xs text-[var(--muted-foreground)]">
                    Daily snapshots: Leg 1 (supplier external) vs Leg 2 (general ledger) vs Leg 3 (operational mirror).
                  </p>
                </div>
                <span className="text-xs text-[var(--muted-foreground)]">
                  Latest run: {reconciliation.latestRun ? new Date(reconciliation.latestRun.runDate).toLocaleDateString('en-ZA') : 'None'}
                </span>
              </div>

              {reconciliation.recentRuns.length === 0 ? (
                <div className="rounded-lg border border-dashed border-[var(--border)] p-8 text-center text-xs text-[var(--muted-foreground)]">
                  No automated 3-way snapshot runs logged for this supplier yet. Open breaks above can still exist from the prefunding and consumption jobs.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-[var(--muted)]/50 text-[var(--muted-foreground)]">
                      <tr>
                        <th className="px-4 py-2.5 font-semibold">Run Date</th>
                        <th className="px-4 py-2.5 text-right font-semibold">Leg 1 (Supplier)</th>
                        <th className="px-4 py-2.5 text-right font-semibold">Leg 2 (Ledger)</th>
                        <th className="px-4 py-2.5 text-right font-semibold">Leg 3 (Mirror)</th>
                        <th className="px-4 py-2.5 text-right font-semibold">Var (1-2)</th>
                        <th className="px-4 py-2.5 text-right font-semibold">Var (2-3)</th>
                        <th className="px-4 py-2.5 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border)] bg-[var(--card)] font-mono">
                      {reconciliation.recentRuns.map((run) => (
                        <tr key={run.id} className="hover:bg-[var(--muted)]/20">
                          <td className="whitespace-nowrap px-4 py-2.5 font-sans text-[var(--foreground)]">
                            {new Date(run.runDate).toLocaleDateString('en-ZA')}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5 text-right text-[var(--foreground)]">
                            {run.leg1SupplierBalance !== null ? formatZAR(run.leg1SupplierBalance) : '—'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5 text-right text-[var(--foreground)]">
                            {formatZAR(run.leg2LedgerBalance)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5 text-right text-[var(--foreground)]">
                            {formatZAR(run.leg3MirrorBalance)}
                          </td>
                          <td
                            className={`whitespace-nowrap px-4 py-2.5 text-right font-bold ${
                              run.varianceSupplierLedger === 0 || run.varianceSupplierLedger === null
                                ? 'text-[#5a8f2a]'
                                : 'text-amber-600'
                            }`}
                          >
                            {run.varianceSupplierLedger !== null ? formatZAR(run.varianceSupplierLedger) : '—'}
                          </td>
                          <td
                            className={`whitespace-nowrap px-4 py-2.5 text-right font-bold ${
                              run.varianceLedgerMirror === 0 ? 'text-[#5a8f2a]' : 'text-[var(--destructive)]'
                            }`}
                          >
                            {formatZAR(run.varianceLedgerMirror)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5 font-sans">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                run.status === 'balanced'
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : 'bg-amber-50 text-amber-700 border border-amber-200'
                              }`}
                            >
                              {run.status.toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 4: Settings & Controls */}
        {activeTab === 'settings' && (
          <div className="p-6 space-y-8">
            {/* 1. Supplier Commercial Banking Details Panel */}
            <div className="rounded-xl border border-[var(--border)] bg-[var(--muted)]/10 p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-[var(--primary)]" />
                    <h3 className="text-sm font-semibold text-[var(--foreground)]">
                      Supplier Commercial Banking Details
                    </h3>
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                      <Lock className="h-2.5 w-2.5" /> OTP Protected
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                    Commercial bank account used for real-time PayShap RPP float top-ups from the Treasury account (1100-01-01).
                  </p>
                </div>
                {isAdmin && !isTreasury && (
                  <button
                    type="button"
                    onClick={openBankModal}
                    className="flex items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-1.5 text-xs font-semibold text-[var(--foreground)] shadow-sm hover:bg-[var(--muted)]"
                  >
                    <Edit3 className="h-3.5 w-3.5 text-[var(--muted-foreground)]" />
                    Edit Banking Details
                  </button>
                )}
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 rounded-lg border border-[var(--border)] bg-[var(--card)] p-4">
                <div>
                  <span className="text-[11px] font-semibold uppercase text-[var(--muted-foreground)]">Bank Name</span>
                  <p className="mt-0.5 text-sm font-medium text-[var(--foreground)]">{supplier.bankName || 'Not configured'}</p>
                </div>
                <div>
                  <span className="text-[11px] font-semibold uppercase text-[var(--muted-foreground)]">Universal Branch Code</span>
                  <p className="mt-0.5 font-mono text-sm font-medium text-[var(--foreground)]">{supplier.bankCode || 'Not configured'}</p>
                </div>
                <div>
                  <span className="text-[11px] font-semibold uppercase text-[var(--muted-foreground)]">Account Number</span>
                  <div className="mt-0.5 flex items-center gap-2">
                    <p className="font-mono text-sm font-medium text-[var(--foreground)]">
                      {showBankFullAcc ? (supplier.bankAccountNumber || 'Not configured') : maskAccountNumber(supplier.bankAccountNumber)}
                    </p>
                    {supplier.bankAccountNumber && (
                      <button
                        type="button"
                        onClick={() => setShowBankFullAcc(!showBankFullAcc)}
                        className="text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                      >
                        {showBankFullAcc ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-[11px] font-semibold uppercase text-[var(--muted-foreground)]">Account Holder</span>
                  <p className="mt-0.5 text-sm font-medium text-[var(--foreground)] truncate">
                    {supplier.bankingDetails?.accountHolderName || supplier.supplierName}
                  </p>
                </div>
                <div>
                  <span className="text-[11px] font-semibold uppercase text-[var(--muted-foreground)]">Statement Reference</span>
                  <p className="mt-0.5 font-mono text-sm font-bold text-[var(--primary)] truncate">
                    {supplier.bankingDetails?.beneficiaryReference || supplier.supplierId}
                  </p>
                  <p className="text-[10px] text-[var(--muted-foreground)]">Supplier reconciliation ref</p>
                </div>
              </div>

              {supplier.bankingDetails?.lastVerifiedAt && (
                <div className="mt-3 flex items-center gap-2 text-xs text-[var(--muted-foreground)]">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#5a8f2a]" />
                  <span>
                    Last verified & authorized by André Botes (+27825571055) via SMS OTP on{' '}
                    {new Date(supplier.bankingDetails.lastVerifiedAt).toLocaleString('en-ZA')}
                  </span>
                </div>
              )}
            </div>

            {/* 2. Threshold Settings Form */}
            <div className="rounded-xl border border-[var(--border)] bg-[var(--muted)]/10 p-5">
              <h3 className="text-sm font-semibold text-[var(--foreground)]">
                Alert Threshold Settings
              </h3>
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                Configure minimum balance floors for automated operational email alerts and dashboard health indicators.
              </p>

              <form onSubmit={handleUpdateThreshold} className="mt-4 max-w-xl space-y-4">
                {thresholdMsg && (
                  <div
                    className={`rounded-lg p-3 text-xs font-medium ${
                      thresholdMsg.type === 'success'
                        ? 'bg-emerald-50 text-emerald-800'
                        : 'bg-red-50 text-red-800'
                    }`}
                  >
                    {thresholdMsg.text}
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)]">
                      Minimum Balance Floor ({balances.unit})
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={minBalanceInput}
                      onChange={(e) => setMinBalanceInput(e.target.value)}
                      disabled={!isAdmin || savingThreshold}
                      className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 font-mono text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)]">
                      Maximum Capacity ({balances.unit}, optional)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={maxBalanceInput}
                      onChange={(e) => setMaxBalanceInput(e.target.value)}
                      disabled={!isAdmin || savingThreshold}
                      placeholder="Uncapped"
                      className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 font-mono text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                    />
                  </div>
                </div>

                {isAdmin ? (
                  <button
                    type="submit"
                    disabled={savingThreshold}
                    className="rounded-lg bg-[var(--primary)] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[var(--primary)]/90 disabled:opacity-50"
                  >
                    {savingThreshold ? 'Saving...' : 'Save Thresholds'}
                  </button>
                ) : (
                  <p className="text-xs text-[var(--muted-foreground)] italic">
                    Admin role required to modify threshold parameters.
                  </p>
                )}
              </form>
            </div>

            {/* 3. Manual Leg-1 Reading Capture */}
            <div className="rounded-xl border border-[var(--border)] bg-[var(--muted)]/10 p-5">
              <h3 className="text-sm font-semibold text-[var(--foreground)]">
                Manual Leg-1 Supplier Reading Capture
              </h3>
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                Record external portal balance readings for suppliers without automated balance query APIs (e.g. Flash). Recording a reading does not change the General Ledger or this float's current balance. It is used only as Leg 1 on the next 3-way compare.
              </p>

              {reconciliation.leg1ManualReading && (
                <div className="mt-3 rounded-lg border border-[var(--border)] bg-[var(--card)] p-3 text-xs">
                  <p className="font-semibold text-[var(--foreground)]">
                    Current Manual Baseline:{' '}
                    <span className="font-mono text-[#5a8f2a]">
                      {formatFloatAmount(reconciliation.leg1ManualReading.balance, balances.unit)}
                    </span>
                  </p>
                  <p className="mt-1 text-[var(--muted-foreground)]">
                    As of: {new Date(reconciliation.leg1ManualReading.asOf).toLocaleString('en-ZA')} ·
                    Captured by: {reconciliation.leg1ManualReading.capturedBy || 'Admin'}
                  </p>
                  {reconciliation.leg1ManualReading.note && (
                    <p className="mt-1 italic text-[var(--foreground)]">
                      "{reconciliation.leg1ManualReading.note}"
                    </p>
                  )}
                </div>
              )}

              <form onSubmit={handleCaptureLeg1} className="mt-4 max-w-xl space-y-4">
                {leg1Msg && (
                  <div
                    className={`rounded-lg p-3 text-xs font-medium ${
                      leg1Msg.type === 'success'
                        ? 'bg-emerald-50 text-emerald-800'
                        : 'bg-red-50 text-red-800'
                    }`}
                  >
                    {leg1Msg.text}
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)]">
                      Supplier-Reported Balance ({balances.unit})
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={leg1BalanceInput}
                      onChange={(e) => setLeg1BalanceInput(e.target.value)}
                      disabled={savingLeg1}
                      placeholder="0.00"
                      required
                      className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 font-mono text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)]">
                      Reading Date / Time
                    </label>
                    <input
                      type="datetime-local"
                      value={leg1AsOfInput}
                      onChange={(e) => setLeg1AsOfInput(e.target.value)}
                      disabled={savingLeg1}
                      className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[var(--foreground)]">
                    Audit Note
                  </label>
                  <input
                    type="text"
                    maxLength={500}
                    value={leg1NoteInput}
                    onChange={(e) => setLeg1NoteInput(e.target.value)}
                    disabled={savingLeg1}
                    placeholder="e.g. Verified via supplier portal dashboard screenshot"
                    className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={savingLeg1}
                  className="rounded-lg bg-[var(--primary)] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[var(--primary)]/90 disabled:opacity-50"
                >
                  {savingLeg1 ? 'Recording...' : 'Record Supplier Reading'}
                </button>
              </form>
            </div>

            {/* 4. Raw Metadata Inspector */}
            <div className="rounded-xl border border-[var(--border)] bg-[var(--muted)]/10 p-5">
              <h3 className="text-sm font-semibold text-[var(--foreground)]">
                Supplier Float Configuration Metadata
              </h3>
              <pre className="mt-3 overflow-x-auto rounded-lg border border-[var(--border)] bg-[var(--card)] p-4 font-mono text-xs text-[var(--foreground)]">
                {JSON.stringify(supplier.metadata || {}, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* 5. Threshold Edit Modal */}
      {showThresholdModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-[var(--border)] bg-[var(--card)] p-6 shadow-xl">
            <h2 className="text-lg font-bold text-[var(--foreground)]">
              Update Minimum Threshold
            </h2>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              {supplier.supplierName} ({supplier.supplierId})
            </p>

            <form onSubmit={handleUpdateThreshold} className="mt-4 space-y-4">
              {thresholdMsg && (
                <div
                  className={`rounded-lg p-3 text-xs font-medium ${
                    thresholdMsg.type === 'success'
                      ? 'bg-emerald-50 text-emerald-800'
                      : 'bg-red-50 text-red-800'
                  }`}
                >
                  {thresholdMsg.text}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-[var(--foreground)]">
                  Minimum Balance Floor ({balances.unit})
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={minBalanceInput}
                  onChange={(e) => setMinBalanceInput(e.target.value)}
                  required
                  className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 font-mono text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[var(--foreground)]">
                  Maximum Capacity ({balances.unit}, optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={maxBalanceInput}
                  onChange={(e) => setMaxBalanceInput(e.target.value)}
                  placeholder="Uncapped"
                  className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 font-mono text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                />
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowThresholdModal(false);
                    setThresholdMsg(null);
                  }}
                  className="rounded-lg border border-[var(--border)] px-4 py-2 text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--muted)]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingThreshold}
                  className="rounded-lg bg-[var(--primary)] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[var(--primary)]/90 disabled:opacity-50"
                >
                  {savingThreshold ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. Pay Supplier (PayShap RPP Top-Up) Modal */}
      {showPayModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-xl border border-[var(--border)] bg-[var(--card)] p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
              <div className="flex items-center gap-2">
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${payStep === 'otp' ? 'bg-amber-100 text-amber-700' : 'bg-[var(--primary)]/10 text-[var(--primary)]'}`}>
                  {payStep === 'otp' ? <Lock className="h-4 w-4" /> : <Zap className="h-4 w-4 fill-current" />}
                </div>
                <div>
                  <h2 className="text-base font-bold text-[var(--foreground)]">
                    {payStep === 'otp' ? 'Executive Authorization Gate' : 'Pay Supplier · PayShap RPP Top-Up'}
                  </h2>
                  <p className="text-xs text-[var(--muted-foreground)]">
                    {payStep === 'otp' ? 'Authorizing PayShap Payment via SMS OTP' : `Instant rail replenishment to ${supplier.supplierName}`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowPayModal(false);
                  setPayOtpSecondsLeft(null);
                }}
                className="text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
              >
                ✕
              </button>
            </div>

            {/* Step 1: Payment Parameters */}
            {payStep === 'form' && (
              <form onSubmit={handleRequestPayOtp} className="mt-4 space-y-4">
                {payMsg && (
                  <div
                    className={`rounded-lg p-3 text-xs font-medium ${
                      payMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
                    }`}
                  >
                    {payMsg.text}
                  </div>
                )}

                {/* Debtor & Creditor Routing Box */}
                <div className="space-y-2 rounded-lg border border-[var(--border)] bg-[var(--muted)]/20 p-3 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[var(--muted-foreground)]">FROM ACCOUNT:</span>
                    <span className="font-mono font-medium text-[var(--foreground)]">1100-01-01 (MMTP Treasury SBSA)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[var(--muted-foreground)]">TO BENEFICIARY:</span>
                    <span className="font-medium text-[var(--foreground)]">{supplier.supplierName}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[var(--muted-foreground)]">DESTINATION BANK:</span>
                    <span className="font-mono font-medium text-[var(--foreground)]">
                      {supplier.bankName || 'Not configured'} ({supplier.bankCode || 'N/A'})
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[var(--muted-foreground)]">ACCOUNT NUMBER:</span>
                    <span className="font-mono font-medium text-[var(--foreground)]">
                      {maskAccountNumber(supplier.bankAccountNumber)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[var(--muted-foreground)]">SUPPLIER STATEMENT REF:</span>
                    <span className="font-mono font-bold text-[var(--primary)]">
                      {supplier.bankingDetails?.beneficiaryReference || supplier.supplierId}
                    </span>
                  </div>
                </div>

                {!supplier.bankAccountNumber && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    <p className="font-semibold">Banking Details Missing</p>
                    <p className="mt-1">
                      This supplier has no commercial bank account on record. Please update banking details first before initiating PayShap payments.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPayModal(false);
                        openBankModal();
                      }}
                      className="mt-2 text-xs font-bold text-[var(--primary)] hover:underline"
                    >
                      Configure Banking Details →
                    </button>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-[var(--foreground)]">
                    Top-Up Amount (ZAR)
                  </label>
                  <div className="relative mt-1.5">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 font-mono text-sm font-bold text-[var(--muted-foreground)]">
                      R
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="1.00"
                      max="50000.00"
                      value={payAmountInput}
                      onChange={(e) => setPayAmountInput(e.target.value)}
                      required
                      placeholder="0.00"
                      className="w-full rounded-lg border border-[var(--border)] bg-[var(--card)] py-2 pl-8 pr-3 font-mono text-base font-bold text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {[1000, 2500, 5000, 10000, 25000].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setPayAmountInput(preset.toFixed(2))}
                        className="rounded border border-[var(--border)] bg-[var(--muted)]/50 px-2 py-0.5 text-[11px] font-mono font-medium text-[var(--foreground)] hover:bg-[var(--muted)]"
                      >
                        +R{preset.toLocaleString('en-ZA')}
                      </button>
                    ))}
                    {balances.minimumBalance > balances.currentBalance && (
                      <button
                        type="button"
                        onClick={() =>
                          setPayAmountInput((balances.minimumBalance - balances.currentBalance).toFixed(2))
                        }
                        className="rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-mono font-medium text-amber-800 hover:bg-amber-100"
                      >
                        Clear Deficit (R{(balances.minimumBalance - balances.currentBalance).toFixed(2)})
                      </button>
                    )}
                  </div>
                  <p className="mt-1.5 text-[11px] text-[var(--muted-foreground)]">
                    Maximum per-transaction limit: <span className="font-semibold font-mono">R 50.000,00</span> (PayShap RPP).
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[var(--foreground)]">
                    Our SBSA statement narrative (optional)
                  </label>
                  <input
                    type="text"
                    maxLength={35}
                    value={payReferenceInput}
                    onChange={(e) => setPayReferenceInput(e.target.value)}
                    placeholder="Leave blank to use TRSUP on our statement"
                    className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none font-mono"
                  />
                  <p className="mt-1 text-[11px] text-[var(--muted-foreground)]">
                    Max 35 characters. Shown on MyMoolah&apos;s SBSA treasury statement only. Leave blank to send the PayShap TRSUP… id. Instant Payment beneficiary reference is the exact saved supplier statement ref. Does not post a second journal.
                  </p>
                </div>

                <div className="rounded-lg border border-[var(--primary)]/20 bg-[var(--primary)]/5 p-3 text-xs text-[var(--foreground)] flex items-start gap-2.5">
                  <ShieldCheck className="h-4 w-4 text-[var(--primary)] shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold">Executive Authorization Gate:</span>
                    <p className="mt-0.5 text-[var(--muted-foreground)]">
                      Clicking below will dispatch a cryptographic SMS OTP to André Botes (+27 82 *** *055) with payload binding.
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPayModal(false);
                      setPayOtpSecondsLeft(null);
                    }}
                    className="rounded-lg border border-[var(--border)] px-4 py-2 text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--muted)]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={requestingPayOtp || !supplier.bankAccountNumber}
                    className="flex items-center gap-1.5 rounded-lg bg-[var(--primary)] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[var(--primary)]/90 disabled:opacity-50"
                  >
                    <KeyRound className="h-3.5 w-3.5" />
                    {requestingPayOtp ? 'Authorizing & Requesting OTP...' : 'Authorize Payment'}
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: OTP Entry & Verification */}
            {payStep === 'otp' && (
              <form onSubmit={handleExecutePayment} className="mt-4 space-y-4">
                {payMsg && (
                  <div
                    className={`rounded-lg p-3 text-xs font-medium ${
                      payMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
                    }`}
                  >
                    {payMsg.text}
                  </div>
                )}

                <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-amber-600" />
                    <h3 className="text-xs font-bold uppercase tracking-wider text-amber-800">
                      Executive SMS OTP Verification
                    </h3>
                  </div>
                  <div className="mt-2 space-y-1 font-mono text-xs text-amber-900">
                    <p>Amount: <span className="font-bold">R {parseFloat(payAmountInput || '0').toFixed(2)}</span></p>
                    <p>Supplier: <span className="font-bold">{supplier.supplierName}</span></p>
                    <p>Bank: <span className="font-bold">{supplier.bankName} (Acc: {maskAccountNumber(supplier.bankAccountNumber)})</span></p>
                    <p>Supplier Statement Ref: <span className="font-bold">{supplier.bankingDetails?.beneficiaryReference || supplier.supplierId}</span></p>
                    <p>Instant Payment beneficiary reference: <span className="font-bold">{supplier.bankingDetails?.beneficiaryReference || supplier.supplierId}</span></p>
                    {payReferenceInput.trim() ? (
                      <p>Our statement narrative: <span className="font-bold">{payReferenceInput.trim()}</span></p>
                    ) : (
                      <p>Our statement narrative: <span className="font-bold">PayShap payment id (TRSUP…)</span></p>
                    )}
                    <p>Rail: <span className="font-bold">SBSA PayShap RPP (PBAC Instant)</span></p>
                  </div>
                </div>

                {payOtpSecondsLeft !== null && (
                  <div className="flex items-center justify-center">
                    {payOtpSecondsLeft > 60 ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 font-mono text-xs font-semibold text-emerald-700">
                        <Clock className="h-3.5 w-3.5" />
                        Expires in {formatOtpCountdown(payOtpSecondsLeft)}
                      </span>
                    ) : payOtpSecondsLeft > 0 ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 font-mono text-xs font-semibold text-amber-800 animate-pulse">
                        <Clock className="h-3.5 w-3.5" />
                        Expiring soon: {formatOtpCountdown(payOtpSecondsLeft)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-50 px-3 py-1 font-mono text-xs font-semibold text-red-700">
                        <AlertCircle className="h-3.5 w-3.5" />
                        OTP Expired (00:00) — Please resend
                      </span>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-center text-xs font-bold uppercase tracking-wider text-[var(--foreground)]">
                    Enter 6-Digit Approval OTP
                  </label>
                  <p className="mt-1 text-center text-xs text-[var(--muted-foreground)]">
                    Check SMS on André's phone ({payOtpPhoneMasked})
                  </p>
                  <input
                    type="text"
                    maxLength={6}
                    autoFocus
                    pattern="[0-9]*"
                    value={payOtpInput}
                    onChange={(e) => setPayOtpInput(e.target.value.replace(/[^0-9]/g, ''))}
                    required
                    disabled={payOtpSecondsLeft === 0}
                    placeholder="••••••"
                    className="mt-3 w-full rounded-xl border-2 border-[var(--primary)] bg-[var(--card)] py-3 text-center font-mono text-2xl font-bold tracking-[0.5em] text-[var(--foreground)] focus:outline-none shadow-inner disabled:opacity-50"
                  />
                </div>

                <div className="flex items-center justify-between text-xs text-[var(--muted-foreground)]">
                  <span>Single-use bcrypt hashed OTP</span>
                  <button
                    type="button"
                    onClick={handleRequestPayOtp}
                    disabled={requestingPayOtp}
                    className="font-semibold text-[var(--primary)] hover:underline"
                  >
                    Resend SMS OTP
                  </button>
                </div>

                {payOtpSecondsLeft === 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-center text-xs text-red-800">
                    OTP has expired. Please click <strong>Resend SMS OTP</strong> to generate a fresh approval code.
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPayStep('form');
                      setPayOtpSecondsLeft(null);
                    }}
                    disabled={executingPay}
                    className="rounded-lg border border-[var(--border)] px-4 py-2 text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--muted)]"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={executingPay || payOtpInput.length !== 6 || payOtpSecondsLeft === 0}
                    className="flex items-center gap-1.5 rounded-lg bg-[var(--primary)] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[var(--primary)]/90 disabled:opacity-50"
                  >
                    {executingPay ? (
                      <>
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                        Executing PayShap RPP Payment...
                      </>
                    ) : (
                      <>
                        <Zap className="h-3.5 w-3.5 fill-current" />
                        Verify & Authorize Payment
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: Success Confirmation */}
            {payStep === 'success' && (
              <div className="mt-4 space-y-4 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h3 className="text-base font-bold text-[var(--foreground)]">
                  PayShap Top-Up Dispatched Successfully!
                </h3>
                <p className="text-xs text-[var(--muted-foreground)]">
                  Funds have been debited from SBSA Treasury (1100-01-01) and credited to the supplier float asset account in real-time.
                </p>

                <div className="space-y-2 rounded-lg border border-[var(--border)] bg-[var(--card)] p-4 text-left font-mono text-xs">
                  <div className="flex justify-between">
                    <span className="text-[var(--muted-foreground)]">Amount:</span>
                    <span className="font-bold text-[#5a8f2a]">R {payResult?.amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--muted-foreground)]">UETR:</span>
                    <span className="font-bold text-[var(--primary)] truncate max-w-[240px]">{payResult?.uetr}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--muted-foreground)]">Journal Ref:</span>
                    <span className="font-bold text-[var(--foreground)]">{payResult?.journalReference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--muted-foreground)]">Updated Balance:</span>
                    <span className="font-bold text-[var(--foreground)]">R {payResult?.updatedBalance?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--muted-foreground)]">Authorized By:</span>
                    <span className="text-[var(--foreground)]">{payResult?.authorizedBy}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowPayModal(false)}
                  className="w-full rounded-lg bg-[var(--primary)] py-2 text-xs font-semibold text-white shadow-sm hover:bg-[var(--primary)]/90"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 7. Edit Supplier Banking Details Modal */}
      {showBankModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-xl border border-[var(--border)] bg-[var(--card)] p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
              <div className="flex items-center gap-2">
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${bankStep === 'otp' ? 'bg-amber-100 text-amber-700' : 'bg-[var(--primary)]/10 text-[var(--primary)]'}`}>
                  {bankStep === 'otp' ? <Lock className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
                </div>
                <div>
                  <h2 className="text-base font-bold text-[var(--foreground)]">
                    {bankStep === 'otp' ? 'Executive Authorization Gate' : 'Supplier Banking Details'}
                  </h2>
                  <p className="text-xs text-[var(--muted-foreground)]">
                    {bankStep === 'otp' ? 'Authorizing Banking Details Change via SMS OTP' : `${supplier.supplierName} (${supplier.supplierId})`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowBankModal(false);
                  setBankOtpSecondsLeft(null);
                }}
                className="text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
              >
                ✕
              </button>
            </div>

            {/* Step 1: Form Inputs */}
            {bankStep === 'form' && (
              <form onSubmit={handleRequestBankOtp} className="mt-4 space-y-4">
                {bankMsg && (
                  <div
                    className={`rounded-lg p-3 text-xs font-medium ${
                      bankMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
                    }`}
                  >
                    {bankMsg.text}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)]">
                      Bank Name
                    </label>
                    <select
                      value={SA_BANKS.some((b) => b.name === bankNameInput) ? bankNameInput : 'custom'}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === 'custom') {
                          setBankNameInput('');
                        } else {
                          const found = SA_BANKS.find((b) => b.name === val);
                          setBankNameInput(val);
                          if (found) setBankCodeInput(found.code);
                        }
                      }}
                      className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                    >
                      {SA_BANKS.map((b) => (
                        <option key={b.name} value={b.name}>
                          {b.name}
                        </option>
                      ))}
                      <option value="custom">Other / Custom Bank</option>
                    </select>
                    {!SA_BANKS.some((b) => b.name === bankNameInput) && (
                      <input
                        type="text"
                        value={bankNameInput}
                        onChange={(e) => setBankNameInput(e.target.value)}
                        placeholder="Enter bank name"
                        required
                        className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)]">
                      Universal Branch Code
                    </label>
                    <input
                      type="text"
                      maxLength={10}
                      value={bankCodeInput}
                      onChange={(e) => setBankCodeInput(e.target.value.replace(/[^0-9]/g, ''))}
                      required
                      placeholder="e.g. 051001"
                      className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 font-mono text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)]">
                      Bank Account Number
                    </label>
                    <input
                      type="text"
                      maxLength={20}
                      value={bankAccountInput}
                      onChange={(e) => setBankAccountInput(e.target.value.replace(/[^0-9]/g, ''))}
                      required
                      placeholder="e.g. 272406481"
                      className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 font-mono text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)]">
                      Account Type
                    </label>
                    <select
                      value={accountTypeInput}
                      onChange={(e) => setAccountTypeInput(e.target.value)}
                      className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                    >
                      <option value="current">Current / Cheque Account</option>
                      <option value="savings">Savings Account</option>
                      <option value="transmission">Transmission Account</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[var(--foreground)]">
                    Account Holder Name
                  </label>
                  <input
                    type="text"
                    maxLength={140}
                    value={accountHolderInput}
                    onChange={(e) => setAccountHolderInput(e.target.value)}
                    required
                    placeholder="e.g. MobileMart Pty Ltd"
                    className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[var(--foreground)]">
                    Supplier Statement Reference (Beneficiary Reference)
                  </label>
                  <input
                    type="text"
                    maxLength={35}
                    value={beneficiaryRefInput}
                    onChange={(e) => setBeneficiaryRefInput(e.target.value)}
                    placeholder={supplier.supplierId}
                    className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 font-mono text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                  />
                  <p className="mt-1 text-[11px] text-[var(--muted-foreground)]">
                    This reference appears on the supplier's bank statement for every PayShap top-up so they can instantly identify and link the payment to MMTP.
                  </p>
                </div>

                <div className="rounded-lg border border-[var(--primary)]/20 bg-[var(--primary)]/5 p-3 text-xs text-[var(--foreground)] flex items-start gap-2.5">
                  <Lock className="h-4 w-4 text-[var(--primary)] shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold">Security Gate:</span>
                    <p className="mt-0.5 text-[var(--muted-foreground)]">
                      To prevent accounts payable fraud, any update to supplier commercial banking details requires SMS OTP approval to André Botes (+27825571055).
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBankModal(false);
                      setBankOtpSecondsLeft(null);
                    }}
                    className="rounded-lg border border-[var(--border)] px-4 py-2 text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--muted)]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={requestingBankOtp}
                    className="flex items-center gap-1.5 rounded-lg bg-[var(--primary)] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[var(--primary)]/90 disabled:opacity-50"
                  >
                    <KeyRound className="h-3.5 w-3.5" />
                    {requestingBankOtp ? 'Saving & Requesting OTP...' : 'Save & Authorize'}
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: OTP Verification */}
            {bankStep === 'otp' && (
              <form onSubmit={handleSaveBankDetails} className="mt-4 space-y-4">
                {bankMsg && (
                  <div
                    className={`rounded-lg p-3 text-xs font-medium ${
                      bankMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
                    }`}
                  >
                    {bankMsg.text}
                  </div>
                )}

                <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-amber-600" />
                    <h3 className="text-xs font-bold uppercase tracking-wider text-amber-800">
                      Authorizing Banking Details Change
                    </h3>
                  </div>
                  <div className="mt-2 space-y-1 font-mono text-xs text-amber-900">
                    <p>Supplier: <span className="font-bold">{supplier.supplierName}</span></p>
                    <p>Bank: <span className="font-bold">{bankNameInput} (Branch: {bankCodeInput})</span></p>
                    <p>Account: <span className="font-bold">{maskAccountNumber(bankAccountInput)}</span></p>
                    <p>Holder: <span className="font-bold">{accountHolderInput}</span></p>
                    <p>Statement Ref: <span className="font-bold">{beneficiaryRefInput || supplier.supplierId}</span></p>
                  </div>
                </div>

                {bankOtpSecondsLeft !== null && (
                  <div className="flex items-center justify-center">
                    {bankOtpSecondsLeft > 60 ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 font-mono text-xs font-semibold text-emerald-700">
                        <Clock className="h-3.5 w-3.5" />
                        Expires in {formatOtpCountdown(bankOtpSecondsLeft)}
                      </span>
                    ) : bankOtpSecondsLeft > 0 ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 font-mono text-xs font-semibold text-amber-800 animate-pulse">
                        <Clock className="h-3.5 w-3.5" />
                        Expiring soon: {formatOtpCountdown(bankOtpSecondsLeft)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-50 px-3 py-1 font-mono text-xs font-semibold text-red-700">
                        <AlertCircle className="h-3.5 w-3.5" />
                        OTP Expired (00:00) — Please resend
                      </span>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-center text-xs font-bold uppercase tracking-wider text-[var(--foreground)]">
                    Enter 6-Digit Approval OTP
                  </label>
                  <p className="mt-1 text-center text-xs text-[var(--muted-foreground)]">
                    Sent via SMS to André's phone ({bankOtpPhoneMasked})
                  </p>
                  <input
                    type="text"
                    maxLength={6}
                    autoFocus
                    pattern="[0-9]*"
                    value={bankOtpInput}
                    onChange={(e) => setBankOtpInput(e.target.value.replace(/[^0-9]/g, ''))}
                    required
                    disabled={bankOtpSecondsLeft === 0}
                    placeholder="••••••"
                    className="mt-3 w-full rounded-xl border-2 border-[var(--primary)] bg-[var(--card)] py-3 text-center font-mono text-2xl font-bold tracking-[0.5em] text-[var(--foreground)] focus:outline-none shadow-inner disabled:opacity-50"
                  />
                </div>

                <div className="flex items-center justify-between text-xs text-[var(--muted-foreground)]">
                  <span>Cryptographic OTP verification</span>
                  <button
                    type="button"
                    onClick={handleRequestBankOtp}
                    disabled={requestingBankOtp}
                    className="font-semibold text-[var(--primary)] hover:underline"
                  >
                    Resend SMS OTP
                  </button>
                </div>

                {bankOtpSecondsLeft === 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-center text-xs text-red-800">
                    OTP has expired. Please click <strong>Resend SMS OTP</strong> to generate a fresh approval code.
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setBankStep('form');
                      setBankOtpSecondsLeft(null);
                    }}
                    disabled={savingBankDetails}
                    className="rounded-lg border border-[var(--border)] px-4 py-2 text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--muted)]"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={savingBankDetails || bankOtpInput.length !== 6 || bankOtpSecondsLeft === 0}
                    className="flex items-center gap-1.5 rounded-lg bg-[var(--primary)] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[var(--primary)]/90 disabled:opacity-50"
                  >
                    {savingBankDetails ? 'Verifying & Saving...' : 'Verify & Save Details'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
